/* Push information to the Google and Matomo dataLayers

This way, a dashboard on Google Analytics/Matomo can be built
to gain more insight on what our users are reading.
*/
const PREFIX = 'js-article';

const AUTHOR_SELECTOR = `.${PREFIX}-author-name`;
const ARTICLE_SELECTOR = '.bo-article-text';
const NUM_COMMENTS_SELECTOR = `.${PREFIX}-comment-count`;
const ISSUE_SELECTOR = `.${PREFIX}-issue-name`;
const IS_COMMUNITY_SELECTOR = `.${PREFIX}-community-warning`;
const TAGS_SELECTOR = `${PREFIX}-tag-item`;
const LINKS_SELECTOR = `${ARTICLE_SELECTOR} a`;
const HIGHLIGHTS_SELECTOR = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'strong',
  'em',
  'b',
  'i',
  'li',
]
  .map(highlight => `${ARTICLE_SELECTOR} ${highlight}`)
  .join(', ');

type MatomoMetrics = string | string[] | number;
type MatomoData = Record<string, MatomoMetrics>;

const matomoData: MatomoData = {};

type parserFn = (text: string) => string | number;
/**
 * Sends data to the Matomo dataLayer
 *
 * It extracts text from an element found using a given querySelector
 * and stores the text in the `matomoData` variable.
 *
 * Optionally one can provide a `parser` function which can be used
 * to transform the text before it is saved.
 * It will save objects that look like this: `{ "key": "selector + parsed text" }`.
 *
 * @param selector: The css selector that will be used to find the text.
 * @param key: The string key under which the text will be recorded in the
 *              data layer.
 * @param parser: Optional function that will be passed the text before it is
 *              written to the data layer.
 */
const track = (
  selector: string,
  key: string,
  parser: parserFn = text => text,
) => {
  const node = document.querySelector(selector);
  if (!node) return;

  const text = node.textContent?.trim();
  if (!text) return;

  const value = parser(text);
  matomoData[key] = value;
};

const trackCount = (selector: string, key: string) => {
  const elements = document.querySelectorAll(selector);

  if (elements.length) {
    matomoData[key] = elements.length;
  }
};

const trackAuthorType = () => {
  const isCommunity = document.querySelector(IS_COMMUNITY_SELECTOR);
  matomoData.article_author_type = isCommunity ? 'COMMUNITY' : 'AUTHOR';
};

const trackArticleTags = () => {
  const tagsElements = document.getElementsByClassName(TAGS_SELECTOR);

  matomoData.article_tags = Array.from(tagsElements)
    .map(element => element.textContent?.trim())
    .filter((tag): tag is string => typeof tag === 'string');
};

interface AnalyticsWindow extends Window {
  _mtm?: MatomoData[];
  dataLayer?: MatomoData[];
}

const behaviorTracking = () => {
  // analyticsWindow variable has type AnalyticsWindow to add access Matomo properties `_mtm` and `dataLayer`
  const analyticsWindow: AnalyticsWindow = window;

  // make sure the `dataLayer` and `_mtm` properties are initialized
  analyticsWindow._mtm = analyticsWindow._mtm || [];
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];

  // run all the tracker functions
  track(AUTHOR_SELECTOR, 'article_author');
  track(NUM_COMMENTS_SELECTOR, 'article_comments', text => parseInt(text, 10));
  track(ISSUE_SELECTOR, 'article_issue');
  trackAuthorType();
  trackArticleTags();
  trackCount(LINKS_SELECTOR, 'article_links');
  trackCount(HIGHLIGHTS_SELECTOR, 'article_highlights');

  // send the data to the dataLayer
  analyticsWindow._mtm.push(matomoData);
  analyticsWindow.dataLayer.push(matomoData);
};

export {
  trackAuthorType,
  trackArticleTags,
  trackCount,
  track,
  matomoData,
  behaviorTracking,
};
